var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { schema } from 'lincd-schema/ontologies/schema';
import { Server } from 'lincd-server-utils/utils/Server';
import { Boolean } from 'lincd-xsd/shapes/Boolean';
import { Literal } from 'lincd/models';
import { disallowProperty, literalProperty, objectProperty, } from 'lincd/shapes/SHACL';
import { Person } from 'profile-plus/shapes/Person';
import { irlcg } from '../ontologies/irlcg.js';
import { linkedShape } from '../package.js';
import { Team } from './Team.js';
let Player = class Player extends Person {
    get currentTeam() {
        return this.getOneAs(irlcg.currentTeam, Team);
    }
    set currentTeam(val) {
        this.overwrite(irlcg.currentTeam, val.namedNode);
    }
    get withoutAuthentication() {
        return Boolean.isTrue(this.getOne(irlcg.withoutAuthentication));
    }
    set withoutAuthentication(val) {
        this.overwrite(irlcg.withoutAuthentication, val ? Boolean.true() : Boolean.false());
    }
    /**
     * Peace Flame (plan 003): denormalized flag indicating this Player has submitted
     * at least one ActionSubmission. Set true on the first submission; never unset.
     * Powers `ActionTotalProvider.getTotalPlayers()` — specifically for the case of
     * GUEST Players (withoutAuthentication = true) created by anonymous Peace Torch
     * visitors. Registered Players are counted regardless of this flag, so it really
     * only matters for the guest path. Lighting the Peace Torch alone does NOT flip
     * this flag; only ActionSubmissions do (e.g. a Befriend report from the torch UI).
     */
    get hasTakenAction() {
        return Boolean.isTrue(this.getOne(irlcg.hasTakenAction));
    }
    set hasTakenAction(val) {
        this.overwrite(irlcg.hasTakenAction, val ? Boolean.true() : Boolean.false());
    }
    get attendance() {
        return this.getValue(irlcg.attendance);
    }
    set attendance(val) {
        this.overwrite(irlcg.attendance, new Literal(val));
    }
    static getSubPlayers() {
        return Server.call(this, 'getSubPlayers');
    }
    get name() {
        return undefined;
    }
    get gender() {
        return undefined;
    }
    get isMarried() {
        return undefined;
    }
    get birthDate() {
        return undefined;
    }
    get birthPlace() {
        return undefined;
    }
    get knows() {
        return undefined;
    }
    get jobTitle() {
        return undefined;
    }
    get honorificPrefix() {
        return undefined;
    }
    get alternateName() {
        return undefined;
    }
    get disambiguatingDescription() {
        return undefined;
    }
    get url() {
        return undefined;
    }
    get additionalType() {
        return undefined;
    }
    get skills() {
        return undefined;
    }
    get hasOccupation() {
        return undefined;
    }
    get homeLocation() {
        return undefined;
    }
    get areaServed() {
        return undefined;
    }
    get description() {
        return undefined;
    }
    get profilePicture2() {
        return undefined;
    }
    get profilePicture3() {
        return undefined;
    }
    get profilePicture4() {
        return undefined;
    }
    get profilePicture5() {
        return undefined;
    }
    get profilePicture6() {
        return undefined;
    }
    get smokingHabit() {
        return undefined;
    }
    get drinkFrequency() {
        return undefined;
    }
    get fourTwentyFriendly() {
        return undefined;
    }
    get languagePreference() {
        return undefined;
    }
    get workoutFrequency() {
        return undefined;
    }
    get passions() {
        return undefined;
    }
    get beliefs() {
        return undefined;
    }
    get systemIdeologies() {
        return undefined;
    }
    get coreValues() {
        return undefined;
    }
    get spiritualPractices() {
        return undefined;
    }
    get sacredTexts() {
        return undefined;
    }
    get mayanAstrology() {
        return undefined;
    }
    get chineseZodiac() {
        return undefined;
    }
    get myersBriggsPersonalityType() {
        return undefined;
    }
    get loveLanguage1() {
        return undefined;
    }
    get loveLanguage2() {
        return undefined;
    }
    get loveLanguage3() {
        return undefined;
    }
    get loveLanguage4() {
        return undefined;
    }
    get loveLanguage5() {
        return undefined;
    }
    get spiritualJourney() {
        return undefined;
    }
    get image() {
        return undefined;
    }
};
Player.targetClass = schema.Person;
__decorate([
    objectProperty({
        path: irlcg.currentTeam,
        shape: Team,
        maxCount: 1,
        description: 'A player can be a member of multiple teams, this represents the current team they are playing under. They are still a valid member of the other teams. Use Team.members for checking membership.',
    }),
    __metadata("design:type", Team),
    __metadata("design:paramtypes", [Team])
], Player.prototype, "currentTeam", null);
__decorate([
    literalProperty({
        path: irlcg.withoutAuthentication,
        datatype: Boolean.targetClass,
        maxCount: 1,
        description: "Player's user account has not been authenticated yet.",
    }),
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [Boolean])
], Player.prototype, "withoutAuthentication", null);
__decorate([
    literalProperty({
        path: irlcg.hasTakenAction,
        datatype: Boolean.targetClass,
        maxCount: 1,
        description: 'True once the Player has made at least one ActionSubmission.',
    }),
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [Boolean])
], Player.prototype, "hasTakenAction", null);
__decorate([
    literalProperty({
        path: irlcg.attendance,
        maxCount: 1,
        // in:[new Literal('inPerson'), new Literal('home')],
        description: 'Way the player will attend events, in person or from home',
    }),
    __metadata("design:type", String),
    __metadata("design:paramtypes", [String])
], Player.prototype, "attendance", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "name", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "gender", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "isMarried", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "birthDate", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "birthPlace", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "knows", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "jobTitle", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "honorificPrefix", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "alternateName", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "disambiguatingDescription", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "url", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "additionalType", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "skills", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "hasOccupation", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "homeLocation", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "areaServed", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "description", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "profilePicture2", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "profilePicture3", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "profilePicture4", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "profilePicture5", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "profilePicture6", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "smokingHabit", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "drinkFrequency", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "fourTwentyFriendly", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "languagePreference", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "workoutFrequency", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "passions", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "beliefs", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "systemIdeologies", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "coreValues", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "spiritualPractices", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "sacredTexts", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "mayanAstrology", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "chineseZodiac", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "myersBriggsPersonalityType", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "loveLanguage1", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "loveLanguage2", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "loveLanguage3", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "loveLanguage4", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "loveLanguage5", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "spiritualJourney", null);
__decorate([
    disallowProperty,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Player.prototype, "image", null);
Player = __decorate([
    linkedShape({
        description: "A person who plays this app. Has current team, profile picture and address. Can be ranked by the number of actions they've taken (gamer, participant, user).",
    })
], Player);
export { Player };
//# sourceMappingURL=Player.js.map